import { createContext, type ReactNode, useEffect, useState } from 'react'
import { WhatsNewModal } from '@/shared/components/changelog/whats-new-modal'
import { devLog, WHATS_NEW_CONFIG } from '@/shared/constants/whats-new-config'

interface WhatsNewContextType {
  currentVersion: string
  showModal: () => void
  hideModal: () => void
  isModalVisible: boolean
}

const WhatsNewContext = createContext<WhatsNewContextType | undefined>(undefined)

interface WhatsNewProviderProps {
  children: ReactNode
  version?: string
  autoShow?: boolean
}

// DX: en desarrollo, `VITE_DISABLE_WHATS_NEW=true` apaga el auto-show del modal de
// Novedades (molesta al navegar/probar). Sólo aplica en dev — en prod nunca se desactiva.
const WHATS_NEW_DISABLED = import.meta.env.DEV && import.meta.env.VITE_DISABLE_WHATS_NEW === 'true'

export const WhatsNewProvider = ({
  children,
  version = WHATS_NEW_CONFIG.CURRENT_VERSION,
  autoShow = WHATS_NEW_CONFIG.SETTINGS.AUTO_SHOW
}: WhatsNewProviderProps) => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const effectiveAutoShow = autoShow && !WHATS_NEW_DISABLED

  const showModal = () => {
    devLog('Showing WhatsNew modal manually')
    setIsModalVisible(true)
  }

  const hideModal = () => {
    devLog('Hiding WhatsNew modal')
    setIsModalVisible(false)
  }

  useEffect(() => {
    if (!effectiveAutoShow) {
      devLog('Auto-show disabled')
      return
    }

    const checkAndShowModal = () => {
      const seenVersion = localStorage.getItem(WHATS_NEW_CONFIG.SETTINGS.STORAGE_KEY)

      devLog('Checking version', { seenVersion, currentVersion: version })

      if (seenVersion !== version) {
        devLog(`User hasn't seen version ${version}, will show modal`)

        // Delay para mejor UX - esperar a que la app se cargue
        const timer = setTimeout(() => {
          devLog('Showing WhatsNew modal automatically')
          setIsModalVisible(true)
        }, WHATS_NEW_CONFIG.SETTINGS.SHOW_DELAY)

        return () => {
          clearTimeout(timer)
        }
      } else {
        devLog(`User already saw version ${version}`)
      }
    }

    checkAndShowModal()
  }, [version, effectiveAutoShow])

  const contextValue: WhatsNewContextType = {
    currentVersion: version,
    showModal,
    hideModal,
    isModalVisible
  }

  return (
    <WhatsNewContext.Provider value={contextValue}>
      {children}
      <WhatsNewModal currentVersion={version} onClose={hideModal} />
    </WhatsNewContext.Provider>
  )
}
